import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { GridContainer, NoMediaFound } from "./MediaGrid.styled";
import MediaCard from "../media-card/MediaCard";
import MediaCardSkeleton from "../media-card-skeleton/MediaCardSkeleton";
import { MediaLibraryStore, withRefluxStore } from "../../../../Stores";
import GlobalMediaLibraryStore from "../../GlobalMediaLibraryStore";
import { ActionsGlobalMediaLibrary } from "../../../../Actions/ActionsGlobalMediaLibrary/ActionsGlobalMediaLibrary";
import { filterMediaAssets } from "../../utils/filterUtils";
import { isVideo } from "../../../SelfServiceCampaignCreation/Previews/utils";
import { SearchBar } from "../../../SelfServiceCampaignCreation/Previews/Components";
import SearchClaims from "../../../SelfServiceCampaignCreation/Previews/Components/SearchClaims";
import { useCallback } from "react";
import Actions from "../../../../Actions";
import logger from "../../../../utils/LoggerFrontend";
import { Group } from "../../../../Components/Layout";
import Terms from "../../../SelfServiceCampaignCreation/Previews/Components/Terms";

// Combined component that shows skeleton while loading and MediaCard when complete
const MediaCardOrSkeleton = ({
  media,
  isVideoAsset,
  isLoading,
  showKPIs = true,
  emitNewImageSelection = null,
  isPopupPreview = false,
  assetPublicId,
  type,
  isLiked,
  showShutterstockSearcher = false
}) => {
  return (
    <motion.div
      key={media ? `media-${media}` : `skeleton-${Math.random()}`}
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{
        duration: 0.4,
        ease: "easeOut"
      }}
      style={{
        width: "300px",
        height: showKPIs ? "291px" : "238px"
      }}
    >
      {isLoading ? (
        <MediaCardSkeleton />
      ) : (
        <MediaCard
          isVideo={isVideoAsset}
          src={media}
          showKPIs={showKPIs}
          emitNewImageSelection={emitNewImageSelection}
          isPopupPreview={isPopupPreview}
          assetPublicId={assetPublicId}
          type={type}
          isLiked={isLiked}
          showShutterstockSearcher={showShutterstockSearcher}
        />
      )}
    </motion.div>
  );
};

// Helper function to get image dimensions
const getImageDimensions = (imageUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      resolve(null);
    };
    img.src = imageUrl;
  });
};

// Helper function to determine aspect ratio
const getAspectRatio = (width, height) => {
  if (!width || !height) return null;
  const ratio = width / height;

  if (ratio > 1.2) return "landscape";
  if (ratio < 0.8) return "portrait";
  if (Math.abs(ratio - 16 / 9) < 0.1) return "16:9";

  return ratio > 1 ? "landscape" : "portrait";
};

const MediaGrid = ({
  filters,
  isPopupPreview = false,
  emitNewImageSelection = null,
  campaignData,
  // Store
  uploadedAssets,
  uploadedLogos,
  oldUploadedImages,
  oldUploadedLogos,
  companyId,
  companyName,
  isUploading,
  totalFiles,
  organization,
  userProfile,
  // Shutterstock
  stock,
  loadingStock
}) => {
  const { t } = useTranslation();
  const showKPIs = filters?.showKPIs !== false;
  const showShutterstockSearcher = filters?.showShutterstock;

  const [showTerms, setShowTerms] = useState(false);
  const [stockSearch, setStockSearch] = useState("");

  useEffect(() => {
    if (companyId && companyName) {
      ActionsGlobalMediaLibrary.getCloudinaryImagesByCompanyId(companyId);
      ActionsGlobalMediaLibrary.getOldUploadedImages(
        organization?.name,
        companyName,
        organization?.id,
        null
      );
      ActionsGlobalMediaLibrary.getOldUploadedLogos(
        organization?.name,
        companyName,
        organization?.id,
        null
      );
    }
  }, [companyId, companyName, organization]);

  const searchShutterStock = useCallback(
    (search) => {
      // If the searchtext didn't change then no need to search for the shutterstock again
      if (search === stockSearch) {
        return;
      }

      const ref = Actions.searchShutterstockImagesAPI.completed.listen((result) => {
        const { payload, query } = result;
        logger.log("TemplateMediaLibrary:searchShutterstockImagesAPI", "images search returned", {
          images: payload.data?.length || 0,
          fallback: payload.fallback,
          search: query.search,
          fallbackSearch: query.fallbackSearch,
          user: { userId: query.userProfile?.userId, userName: query.userProfile?.userName },
          companyName: companyName,
          orgaName: organization?.name
        });
        ref();
      });

      const refFailed = Actions.searchShutterstockImagesAPI.failed.listen((error) => {
        logger.log(
          "error:TemplateMediaLibrary:searchShutterstockImagesAPI",
          "images search failed",
          {
            error: error?.meta?.message || error.message,
            fallback: error.fallback,
            search: error.search,
            fallbackSearch: error.fallbackSearch,
            userProfile: error.userProfile,
            companyName: companyName,
            orgaName: organization?.name
          }
        );
        refFailed();
      });

      if (search) {
        Actions.searchShutterstockImagesAPI({
          search: search || campaignData?.jobTitle,
          fallbackSearch: "",
          userProfile,
          version: 2
        });

        setStockSearch(search);
      }
    },
    [userProfile, companyName, organization?.name, campaignData?.jobTitle, stockSearch]
  );

  const allImages = Array.from(
    new Set([...uploadedAssets, ...oldUploadedImages, ...uploadedLogos, ...oldUploadedLogos])
  );
  const renderImages = showShutterstockSearcher ? stock : allImages;

  // If showing Shutterstock searcher, map stock to MediaCard-compatible objects
  const mappedRenderImages = showShutterstockSearcher
    ? (renderImages || []).map((item) => ({
        image_url: item.assets?.huge_thumb?.url,
        assetPublicId: item.id || item.assets?.huge_thumb?.url,
        type: "image",
        isLiked: false,
        _original: item
      }))
    : renderImages;

  // Filter the images based on the selected filters
  const filteredImages = useMemo(() => {
    return filterMediaAssets(mappedRenderImages, filters);
  }, [mappedRenderImages, filters]);

  return (
    <>
      {filteredImages.length === 0 && !isUploading && (
        <NoMediaFound>{t("No media found for this company")}</NoMediaFound>
      )}

      {showShutterstockSearcher && (
        <Group gap={"10px"} direction="column">
          <div style={{ width: "100%" }}>
            <SearchBar
              defaultText={campaignData?.jobTitle}
              debounce={1000}
              onChange={searchShutterStock}
            />
          </div>
          <div style={{ width: "100%" }}>
            <SearchClaims onClick={() => setShowTerms(!showTerms)} />
          </div>

          {showTerms && (
            <div style={{ width: "100%" }}>
              <Terms />
            </div>
          )}
        </Group>
      )}

      <GridContainer showKPIs={showKPIs} isPopupPreview={isPopupPreview}>
        {isUploading && totalFiles > 0 && (
          <>
            {Array.from({ length: totalFiles }, (_, index) => (
              <MediaCardOrSkeleton
                key={`upload-${index}`}
                media={null}
                isVideoAsset={false}
                isLoading={true}
                showKPIs={showKPIs}
              />
            ))}
          </>
        )}

        {loadingStock && showShutterstockSearcher && (
          <>
            {Array.from({ length: 10 }, (_, index) => (
              <MediaCardSkeleton key={`loading-${index}`} />
            ))}
          </>
        )}

        {!showTerms &&
          filteredImages.map((media, index) => {
            const mediaUrl = media.image_url;
            const isVideoAsset = isVideo(mediaUrl);
            const assetPublicId = media.name;
            const type = isVideoAsset ? "video" : "image";
            const isLiked = media.isLiked;

            return (
              <MediaCardOrSkeleton
                key={`existing-${index}`}
                media={mediaUrl}
                isVideoAsset={isVideoAsset}
                isLoading={false}
                showKPIs={showKPIs}
                emitNewImageSelection={emitNewImageSelection}
                isPopupPreview={isPopupPreview}
                assetPublicId={assetPublicId}
                type={type}
                isLiked={isLiked}
                showShutterstockSearcher={showShutterstockSearcher}
              />
            );
          })}
      </GridContainer>
    </>
  );
};

export default withRefluxStore(
  MediaGrid,
  [GlobalMediaLibraryStore, MediaLibraryStore],
  [
    "uploadedAssets",
    "uploadedLogos",
    "oldUploadedImages",
    "oldUploadedLogos",
    "companyId",
    "companyName",
    "isUploading",
    "totalFiles",
    "organization",
    "userProfile",
    "stock",
    "loadingStock"
  ]
);
